import { useEffect, useState, useCallback, useRef } from 'react';
import type { BreakpointAliasKey } from '@withjoy/joykit/theme';
import type { Screen as JoykitScreen } from '@shared/joykit/packages/core/utils/breakpoints';
import { breakpointsByScreen as joykitBreakpointsBySize } from '@shared/joykit/packages/core/utils/breakpoints';
import type { ScreenBreakpoints } from '@shared/utils/style/breakpoints';
import { breakpoints } from '@shared/utils/style/breakpoints';
import { withWindow } from '../withWindow';
import type { MediaScreen } from '@shared/utils/media/DefaultMediaMatchProvider';
import { useDefaultMediaMatchContext } from '@shared/utils/media/DefaultMediaMatchProvider';
import { mediaScreenWidths } from '@shared/utils/media/mediaScreenWidths.constants';

type ScreenBreakpoint = string | 0;
const screenBreakpoins: ScreenBreakpoints = breakpoints;
function breakpointsByScreen(screen: BreakpointAliasKey | JoykitScreen | string) {
  if (screen in screenBreakpoins) {
    return screenBreakpoins[screen as BreakpointAliasKey];
  }
  if (screen.endsWith('rem')) {
    return screen;
  }
  return joykitBreakpointsBySize(screen as JoykitScreen);
}

export interface UseMediaArgs<T> {
  values: Partial<Record<BreakpointAliasKey | JoykitScreen | string, T>>;
}

/**
 * Convers a rem value to an int for sorting
 * Ex: 24rem => 24
 * @param val
 */
const convertRemToInt = (val: ScreenBreakpoint): number => {
  if (typeof val === 'string') {
    return Number(val.slice(0, -3));
  }
  return 0;
};

interface MediaQueryObject<T> {
  media: string;
  value: T;
}

/**
 * Converts the list of values and screens into an ordered list of css media queries and values
 * @param values
 */
const orderRemValues = <T>(values: Partial<Record<BreakpointAliasKey | JoykitScreen, T>>): Array<MediaQueryObject<T>> => {
  return Object.entries(values)
    .map(entries => ({ value: entries[1] as T, size: breakpointsByScreen(entries[0]) }))
    .sort((a, b) => (convertRemToInt(a.size as ScreenBreakpoint) < convertRemToInt(b.size as ScreenBreakpoint) ? -1 : 1))
    .map(entry => ({ media: `(min-width: ${entry.size})`, value: entry.value }));
};

const MIN_WIDTH_REGEX = /min-width:\s*([\d.]+)/;

/**
 * Gets the value of the largest matching media query
 * @param media
 * @param orderedMediaList
 */
const getCalculatedValue = <T>(media: MediaQueryList[], orderedMediaList: Array<MediaQueryObject<T>>) => {
  return media.reduce((acc: T | undefined, curr: MediaQueryList) => {
    const val = orderedMediaList.find(val => val.media === curr.media);
    if (val && curr.matches) {
      return val.value;
    } else {
      return acc;
    }
  }, undefined);
};

/**
 * Derive an SSR-safe initial value from the already-resolved ordered media list.
 * Uses the pre-computed breakpoint values from orderRemValues() to avoid calling
 * breakpointsByScreen() again (which invokes useTheme() — a hook that can't be
 * called from a non-hook function).
 *
 * Simulates what getCalculatedValue would return by checking which breakpoints
 * the representative screen width would satisfy via min-width matching.
 */
function deriveInitialValueFromOrderedMediaList<T>(orderedMediaList: Array<MediaQueryObject<T>>, defaultScreen: MediaScreen): T | undefined {
  const screenWidthRem = mediaScreenWidths[defaultScreen];

  let result: T | undefined;
  for (const entry of orderedMediaList) {
    // entry.media is e.g. "(min-width: 23.4375rem)" or "(min-width: 0em)"
    // Extract the numeric value — supports both rem and em units
    const match = entry.media.match(MIN_WIDTH_REGEX);
    if (match) {
      const breakpointValue = parseFloat(match[1]);
      if (breakpointValue <= screenWidthRem) {
        result = entry.value;
      }
    }
  }
  return result;
}

const isPlainObjectOrArray = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null || Array.isArray(value);
};

const isShallowEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (!isPlainObjectOrArray(a) || !isPlainObjectOrArray(b) || Array.isArray(a) !== Array.isArray(b)) return false;
  // Sparse arrays can share enumerable keys while differing in length ([x] vs [x, ,]).
  if (Array.isArray(a) && Array.isArray(b) && a.length !== b.length) return false;
  const keysA = Object.keys(a);
  return keysA.length === Object.keys(b).length && keysA.every(key => Object.prototype.hasOwnProperty.call(b, key) && Object.is(a[key], b[key]));
};

/**
 * Returns a value based on the current size of the screen.
 * A value can be assigned to a screen size which will return if that screen size matches the current window size.
 * Not all screen sizes need to be used, it will default to the largest matching media query.
 *
 * During SSR, when no explicit `initialValue` is provided, the hook derives a default from
 * `useDefaultMediaMatchContext().defaultScreen` (set from the request's User-Agent).
 * This prevents hydration mismatches where SSR renders with a wrong breakpoint default.
 *
 * @param args
 */
function useResponsive<T>(args: UseMediaArgs<T>): [T | undefined];
function useResponsive<T>(args: UseMediaArgs<T>, initialValue: T): [T];
function useResponsive<T>(args: UseMediaArgs<T>, initialValue?: T): [T | undefined] {
  const { values } = args;
  const mediaMatchContext = useDefaultMediaMatchContext();

  const orderedMediaListRef = useRef(orderRemValues(values));

  const getMatch = useCallback((targetWindow: Window, mediaQueryList: Array<MediaQueryObject<T>>) => {
    const media = mediaQueryList.map(item => targetWindow.matchMedia(item.media));
    return getCalculatedValue(media, mediaQueryList);
  }, []);

  // When no explicit initialValue is provided AND a DefaultMediaMatchProvider is present,
  // derive an SSR-safe initial value from the device screen context.
  // When no provider is present (e.g., tests), fall back to the original behavior (initialValue).
  const effectiveInitialValue =
    initialValue !== undefined
      ? initialValue
      : mediaMatchContext?.defaultScreen
        ? deriveInitialValueFromOrderedMediaList(orderedMediaListRef.current, mediaMatchContext.defaultScreen)
        : undefined;

  const [currentValue, setCurrentValue] = useState<T | undefined>(effectiveInitialValue);

  const updateCurrentValue = useCallback((value: T | undefined) => {
    if (typeof value !== 'undefined') {
      // Callers usually pass fresh object literals, so a content-equal match must not trigger a re-render.
      setCurrentValue(prev => (isShallowEqual(prev, value) ? prev : value));
    }
  }, []);

  useEffect(() => {
    return withWindow(
      global => {
        const orderedMediaList = orderedMediaListRef.current;
        updateCurrentValue(getMatch(global, orderedMediaList));

        const handleResize = () => {
          updateCurrentValue(getMatch(global, orderedMediaList));
        };

        global.addEventListener('resize', handleResize);
        return () => global.removeEventListener('resize', handleResize);
      },
      () => {},
    );
  }, [updateCurrentValue, getMatch]);

  return [currentValue];
}

export { useResponsive };
